import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getMcpStats,
  getMcpToolStats,
  getMcpUserStats,
  getMcpAnomalies,
  getMcpCalls,
  McpStatsDTO,
  McpToolStatsDTO,
  McpUserStatsDTO,
  McpAnomalyDTO,
  McpCallDTO,
} from '../services/mcpAuditService';

// ── Helpers ──────────────────────────────────────────────────────────────────

const pct = (rate: number) => `${(rate * 100).toFixed(1)}%`;

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const anomalyChipColor = (severity: string): 'error' | 'warning' | 'default' => {
  if (severity === 'red') return 'error';
  if (severity === 'orange') return 'warning';
  return 'default';
};

const anomalyIcon = (severity: string) => {
  if (severity === 'red') return <ErrorIcon fontSize="small" />;
  if (severity === 'orange') return <WarningIcon fontSize="small" />;
  return <InfoIcon fontSize="small" />;
};

// ── Sub-sections ─────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub }) => (
  <Card elevation={2}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h4" component="div" fontWeight="bold">
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ── Overview tab ──────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{ stats: McpStatsDTO | null; loading: boolean }> = ({
  stats,
  loading,
}) => {
  if (loading) return <CircularProgress sx={{ mt: 4 }} />;
  if (!stats) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Calls today" value={stats.callsToday.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Calls (7d)" value={stats.calls7d.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Calls (30d)" value={stats.calls30d.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard label="Active users (30d)" value={stats.uniqueUsers30d.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Error rate (30d)"
            value={pct(stats.errorRate30d)}
            sub={stats.errorRate30d > 0.3 ? '⚠ High' : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Top tools */}
        <Grid item xs={12} md={4}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Tools (30d)
              </Typography>
              {stats.topTools.map((t) => (
                <Box
                  key={t.name}
                  display="flex"
                  justifyContent="space-between"
                  py={0.5}
                  borderBottom="1px solid #f0f0f0"
                >
                  <Typography variant="body2">{t.name}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {t.count.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top users */}
        <Grid item xs={12} md={4}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Users (30d)
              </Typography>
              {stats.topUsers.map((u) => (
                <Box
                  key={u.name}
                  display="flex"
                  justifyContent="space-between"
                  py={0.5}
                  borderBottom="1px solid #f0f0f0"
                >
                  <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                    <Tooltip title={u.name}><span>{u.name}</span></Tooltip>
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {u.count.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top clients */}
        <Grid item xs={12} md={4}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Clients (30d)
              </Typography>
              {stats.topClients.map((c) => (
                <Box
                  key={c.name}
                  display="flex"
                  justifyContent="space-between"
                  py={0.5}
                  borderBottom="1px solid #f0f0f0"
                >
                  <Typography variant="body2">{c.name}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {c.count.toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// ── Anomalies tab ─────────────────────────────────────────────────────────────

const AnomaliesTab: React.FC<{ anomalies: McpAnomalyDTO[]; loading: boolean }> = ({
  anomalies,
  loading,
}) => {
  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  if (anomalies.length === 0) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        No anomalies detected in the recent audit log.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {anomalies.length} anomal{anomalies.length === 1 ? 'y' : 'ies'} detected
      </Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Tool</TableCell>
              <TableCell>Count</TableCell>
              <TableCell>Last seen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {anomalies.map((a, idx) => (
              <TableRow
                key={idx}
                sx={{
                  backgroundColor:
                    a.severity === 'red'
                      ? 'rgba(244,67,54,0.06)'
                      : a.severity === 'orange'
                      ? 'rgba(255,152,0,0.06)'
                      : 'rgba(255,235,59,0.06)',
                }}
              >
                <TableCell>
                  <Chip
                    icon={anomalyIcon(a.severity)}
                    label={a.severity.toUpperCase()}
                    color={anomalyChipColor(a.severity)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {a.anomalyType}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{a.description}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{a.affectedUserEmail ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {a.affectedToolName ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>{a.count}</TableCell>
                <TableCell>
                  <Typography variant="caption">{fmtDate(a.lastSeenAt)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Tools tab ─────────────────────────────────────────────────────────────────

const ToolsTab: React.FC<{ tools: McpToolStatsDTO[]; loading: boolean }> = ({
  tools,
  loading,
}) => {
  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <TableContainer component={Paper} elevation={1} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tool name</TableCell>
            <TableCell align="right">Total calls</TableCell>
            <TableCell align="right">Avg duration (ms)</TableCell>
            <TableCell align="right">Error rate</TableCell>
            <TableCell>Last called</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tools.map((t) => (
            <TableRow key={t.toolName}>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {t.toolName}
                </Typography>
              </TableCell>
              <TableCell align="right">{t.totalCalls.toLocaleString()}</TableCell>
              <TableCell align="right">{t.avgDurationMs.toFixed(0)}</TableCell>
              <TableCell align="right">
                <Chip
                  label={pct(t.errorRate)}
                  color={t.errorRate > 0.3 ? 'error' : t.errorRate > 0.1 ? 'warning' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="caption">{fmtDate(t.lastCalledAt)}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Users tab ─────────────────────────────────────────────────────────────────

const UsersTab: React.FC<{ users: McpUserStatsDTO[]; loading: boolean }> = ({
  users,
  loading,
}) => {
  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <TableContainer component={Paper} elevation={1} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>User email</TableCell>
            <TableCell align="right">Total calls</TableCell>
            <TableCell>Most used tool</TableCell>
            <TableCell>Last active</TableCell>
            <TableCell align="right">Error rate</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <Typography variant="body2">{u.userEmail}</Typography>
              </TableCell>
              <TableCell align="right">{u.totalCalls.toLocaleString()}</TableCell>
              <TableCell>
                <Typography variant="caption" fontFamily="monospace">
                  {u.mostUsedTool ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption">{fmtDate(u.lastActiveAt)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={pct(u.errorRate)}
                  color={u.errorRate > 0.8 ? 'error' : u.errorRate > 0.3 ? 'warning' : 'default'}
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Call log tab ──────────────────────────────────────────────────────────────

const CallLogTab: React.FC = () => {
  const [calls, setCalls] = useState<McpCallDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [toolNameFilter, setToolNameFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(0);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMcpCalls({
        userEmail: userEmailFilter || undefined,
        toolName: toolNameFilter || undefined,
        success: successFilter === '' ? undefined : successFilter === 'true',
        page,
        size: 50,
      });
      setCalls(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load call log');
    } finally {
      setLoading(false);
    }
  }, [userEmailFilter, toolNameFilter, successFilter, page]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Filters */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <TextField
          label="User email"
          size="small"
          value={userEmailFilter}
          onChange={(e) => { setUserEmailFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Tool name"
          size="small"
          value={toolNameFilter}
          onChange={(e) => { setToolNameFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={successFilter}
            label="Status"
            onChange={(e: SelectChangeEvent<'' | 'true' | 'false'>) => {
              setSuccessFilter(e.target.value as '' | 'true' | 'false');
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Success</MenuItem>
            <MenuItem value="false">Failed</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchCalls}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Tool</TableCell>
                  <TableCell>Called at</TableCell>
                  <TableCell align="right">Duration (ms)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{c.userEmail ?? 'anonymous'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{c.clientName ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {c.toolName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmtDate(c.calledAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">{c.durationMs ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={c.success ? 'OK' : 'FAIL'}
                        color={c.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {c.errorMessage ? (
                        <Tooltip title={c.errorMessage}>
                          <Typography
                            variant="caption"
                            color="error"
                            noWrap
                            sx={{ maxWidth: 200, display: 'block' }}
                          >
                            {c.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {calls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Simple prev/next pagination */}
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Typography variant="body2" alignSelf="center">
              Page {page + 1}
            </Typography>
            <Button
              size="small"
              disabled={calls.length < 50}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

/**
 * AdminMcpPage — System Admin MCP Server monitoring page.
 *
 * Sections (tabs):
 *   1. Anomaly Alerts (primary ops view — shown first)
 *   2. Overview cards + top-N lists
 *   3. Tools breakdown table
 *   4. Users breakdown table
 *   5. Raw call log with filters
 */
const AdminMcpPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  const [stats, setStats] = useState<McpStatsDTO | null>(null);
  const [tools, setTools] = useState<McpToolStatsDTO[]>([]);
  const [users, setUsers] = useState<McpUserStatsDTO[]>([]);
  const [anomalies, setAnomalies] = useState<McpAnomalyDTO[]>([]);

  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAnomalies, setLoadingAnomalies] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    setLoadingStats(true);
    setLoadingTools(true);
    setLoadingUsers(true);
    setLoadingAnomalies(true);

    const [statsRes, toolsRes, usersRes, anomaliesRes] = await Promise.allSettled([
      getMcpStats(),
      getMcpToolStats(),
      getMcpUserStats(),
      getMcpAnomalies(),
    ]);

    if (statsRes.status === 'fulfilled') setStats(statsRes.value);
    else setError('Failed to load summary stats');
    setLoadingStats(false);

    if (toolsRes.status === 'fulfilled') setTools(toolsRes.value);
    setLoadingTools(false);

    if (usersRes.status === 'fulfilled') setUsers(usersRes.value);
    setLoadingUsers(false);

    if (anomaliesRes.status === 'fulfilled') setAnomalies(anomaliesRes.value);
    setLoadingAnomalies(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            MCP Server
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Audit log analytics for AI agent tool usage
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAll}
        >
          Refresh all
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <WarningIcon fontSize="small" />
              Anomaly Alerts
              {anomalies.length > 0 && (
                <Chip
                  label={anomalies.length}
                  color={anomalies.some((a) => a.severity === 'red') ? 'error' : 'warning'}
                  size="small"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
          }
        />
        <Tab label="Overview" />
        <Tab label="Tools" />
        <Tab label="Users" />
        <Tab label="Call Log" />
      </Tabs>

      {tab === 0 && <AnomaliesTab anomalies={anomalies} loading={loadingAnomalies} />}
      {tab === 1 && <OverviewTab stats={stats} loading={loadingStats} />}
      {tab === 2 && <ToolsTab tools={tools} loading={loadingTools} />}
      {tab === 3 && <UsersTab users={users} loading={loadingUsers} />}
      {tab === 4 && <CallLogTab />}
    </Container>
  );
};

export default AdminMcpPage;
